import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ModerationActions } from '../../../../../models';

import { TransitionActionHandler } from './handler/ActionsHandler';
import { handleSearchQuery } from '../../../../manager/components/context';
import { useCurrentPageFilters } from '../../../../../utils/filters';
import { capitalizeFirstLetter } from '../../../../attendee/detail/components/utils';

import { Card, ContentCard, Dropdown, DropdownItem, FilterSetController, Icon, StopPropagation, Table, ToastContext, TooltipHelp, Filter, Status, Message } from '@weezevent/nacre';
import ConfirmModal from '../../../../../components/confirmModal';
import LoadingTable from '../../../../../components/loadingTable';
import ModerationPaginator from '../../../components/moderationPaginator';

const spec = {
    multiple: [],
    fields: ['search'],
    aliases: {}
};

export default function TransitionsTab({ organization, flowId, path }) {
    const defaultFilters = {};
    const history = useHistory();
    const { t } = useTranslation();
    const [pageSize] = React.useState(100);
    const toast = React.useContext(ToastContext);
    const [currentPage, setCurrentPage] = React.useState(0);
    const [filters, setFilters] = useCurrentPageFilters(spec);
    const [actionToDelete, setActionToDelete] = React.useState(null);

    const [actions, loadingActions, syncActions] = ModerationActions.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            cache: false,
            allow_cache: false,
            query: {
                limit: pageSize,
                offset: currentPage * pageSize,
                ...handleSearchQuery(spec, filters)
            }
        },
        [organization, flowId, filters, currentPage, handleSearchQuery]
    );

    const typesOptions = TransitionActionHandler.get_allowed_actions(organization.id).map((act, index) => ({
        key: index,
        text: t(`ticket.moderation.statusesTransitions.transition.steps.actions.${act}`),
        name: act
    }));

    const handleSearch = React.useCallback(
        value => {
            setFilters({ ...filters, search: value });
        },
        [filters]
    );

    const handleStatus = React.useCallback(
        (action, fn) => {
            action[fn]({ organizationId: organization.id, flowId }).then(() => syncActions());
        },
        [syncActions, organization.id, flowId]
    );

    const onDelete = React.useCallback(
        action => {
            action
                .delete({ organizationId: organization.id, flowId })
                .then(() => {
                    toast.success(t(`ticket.moderation.statusesTransitions.actionsTable.actionDelete.toastSuccess`));
                    setActionToDelete(null);
                    syncActions();
                })
                .catch(() => {
                    toast.error(t(`ticket.moderation.statusesTransitions.actionsTable.actionDelete.toastError`));
                });
        },
        [organization, flowId, syncActions]
    );

    return (
        <Card>
            <ContentCard>
                <FilterSetController
                    total={actions?.length}
                    isLoadingTotal={loadingActions}
                    labelTotal={t('ticket.moderation.statusesTransitions.actionsTable.countTotal', { count: actions?.length || 0 })}
                    onChange={values => setFilters(values)}
                    initialValues={filters}
                    defaultValues={defaultFilters}
                    onReset={() => {}}
                    search={{
                        value: filters?.search,
                        onChange: (_, search = '') => handleSearch(search),
                        placeholder: t('ticket.moderation.common.searchPlaceholder'),
                        handleReset: () => setFilters(filters => ({ ...filters, search: '' }))
                    }}
                >
                    <Filter index={0} name="type" options={typesOptions} labelName={t('ticket.common.type')} />
                    <Filter
                        index={1}
                        name="active"
                        options={[
                            { text: t('ticket.common.active'), name: 'true' },
                            { text: t('ticket.common.inactive'), name: 'false' }
                        ]}
                        labelName={t('ticket.common.status')}
                    />
                </FilterSetController>
            </ContentCard>
            <Table useNew>
                <Table.Header>
                    <Table.Tr>
                        <th>{t('ticket.moderation.statusesTransitions.actionsTable.name')}</th>
                        <th>{t('ticket.moderation.statusesTransitions.actionsTable.type')}</th>
                        <th>{t('ticket.moderation.statusesTransitions.actionsTable.mandatory')}</th>
                        <th>
                            {t('ticket.moderation.statusesTransitions.actionsTable.amount')}
                            <TooltipHelp title={t('ticket.moderation.statusesTransitions.actionsTable.amount_tooltip')} />
                        </th>
                        <th>
                            {t('ticket.moderation.statusesTransitions.actionsTable.status')}
                            <TooltipHelp title={t('ticket.moderation.statusesTransitions.actionsTable.status_tooltip')} />
                        </th>
                        <th>{t('ticket.moderation.statusesTransitions.actionsTable.actions')}</th>
                    </Table.Tr>
                </Table.Header>
                {!!actions?.length && !loadingActions && (
                    <Table.TBody>
                        {actions
                            ?.sort((a, b) => a.name?.localeCompare(b?.name))
                            .map(action => (
                                <ActionsRow
                                    key={action.id}
                                    action={action}
                                    flowId={flowId}
                                    organization={organization}
                                    handleStatus={handleStatus}
                                    setActionToDelete={setActionToDelete}
                                />
                            ))}
                    </Table.TBody>
                )}
            </Table>
            <LoadingTable loading={loadingActions} height="30vh" />
            {!loadingActions && !actions?.length && (
                <Card.EmptyState
                    icon={<Icon name="list" />}
                    onClick={() => history.push(`${path}/actions/new`)}
                    title={t('ticket.moderation.statusesTransitions.actionsTable.emptyTitle')}
                    labelButton={t('ticket.moderation.statusesTransitions.actionsTable.emptyCta')}
                    subtitle={t('ticket.moderation.statusesTransitions.actionsTable.emptySubtitle')}
                />
            )}
            {actionToDelete && (
                <ConfirmModal
                    size="large"
                    labelTitle={t('ticket.moderation.statusesTransitions.actionsTable.actionDelete.modalTitle')}
                    labelBody={
                        <Message type="warning" title={t('ticket.moderation.statusesTransitions.actionsTable.deletion_modal.title')}>
                            {t('ticket.moderation.statusesTransitions.actionsTable.deletion_modal.content')}
                        </Message>
                    }
                    objectToSave={actionToDelete}
                    onClose={() => setActionToDelete(false)}
                    onAccept={onDelete}
                />
            )}
            <ModerationPaginator currentPage={currentPage} setCurrentPage={setCurrentPage} totalCount={+actions?.__meta?.headers?.get('total-count') || 0} pageSize={pageSize} />
        </Card>
    );
}

const ActionsRow = ({ action, organization, flowId, setActionToDelete, handleStatus }) => {
    const { t } = useTranslation();
    const history = useHistory();

    return (
        <Table.Tr onClick={() => history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/actions/${action.id}`)}>
            <Table.Td>{capitalizeFirstLetter(action.name)}</Table.Td>
            <Table.Td>{t(`ticket.moderation.statusesTransitions.transition.steps.actions.${action.type}`)}</Table.Td>
            <Table.Td>{t(`ticket.moderation.statusesTransitions.actionsTable.${action.mode}`)}</Table.Td>
            <Table.Td>{action.transitions.length}</Table.Td>
            <Table.Td>
                <StopPropagation>
                    <Status.Dropdown
                        color={action.status_color}
                        items={[
                            <DropdownItem
                                key="0"
                                disabled={action.active}
                                onClick={() => handleStatus(action, 'activate')}
                                item={t('ticket.moderation.statusesTransitions.actionsTable.status_activate')}
                            />,
                            <DropdownItem
                                key="1"
                                disabled={!action.active}
                                onClick={() => handleStatus(action, 'deactivate')}
                                item={t('ticket.moderation.statusesTransitions.actionsTable.status_deactivate')}
                            />
                        ]}
                        text={t(`ticket.moderation.statusesTransitions.actionsTable.status_${action.status}`)}
                    />
                </StopPropagation>
            </Table.Td>
            <Table.Td>
                <Dropdown closeOnDocumentClick size="medium">
                    <StopPropagation>
                        <DropdownItem
                            item={t('ticket.moderation.statusesTransitions.actionsTable.actionEdit')}
                            onClick={() => history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/actions/${action.id}`)}
                        />
                        <DropdownItem
                            deleteItem
                            onClick={() => setActionToDelete(action)}
                            item={t('ticket.moderation.statusesTransitions.actionsTable.actionDelete.title')}
                            disabled={action.is_applied}
                        />
                    </StopPropagation>
                </Dropdown>
            </Table.Td>
        </Table.Tr>
    );
};
